// eslint-disable-next-line import/no-cycle
import { ArtifactType } from './artifact-digest-types';
import { Task } from './client-intake-types';
import { BaseMatterNote } from './matter-notes-types';

export type ProviderPortalType = 'SMARTREQUEST' | 'MRO' | 'CHARTSWAP';

export type ProviderTypeHint = 'HOSPITAL_SYSTEM' | 'CLINIC_INDEPENDENT';

export type ProviderServiceType =
  | 'ER'
  | 'RADIOLOGY'
  | 'CHIROPRACTIC'
  | 'ORTHOPEDIC'
  | 'PHYSICAL_THERAPY'
  | 'PRIMARY_CARE'
  | 'PHARMACY'
  | 'IMAGING'
  | 'SURGERY'
  | 'SPECIALIST'
  | 'GENERAL';

export interface ApproveMedicalProviderRequest {
  name?: string;
  address?: string | null;
  provider_type_hint?: ProviderTypeHint;
  // Backward-compatible temporary input accepted by backend.
  service_type?: ProviderServiceType | 'HOSPITAL' | 'CLINIC';
}

export interface SavedProviderSummary {
  id: string;
  name: string;
  normalized_name?: string | null;
  portal_eligible?: boolean | null;
  portal_type?: ProviderPortalType | null;
}

export interface MedicalProviderStub {
  id: string;
  name: string | null;
  address?: string | null;
  treatment_status: ProviderTreatmentStatus;
  date_of_records_request?: string | null;
}
export interface MedicalProvider extends BaseMatterNote {
  /** Name of the medical provider */
  name: string | null;
  /**  Address of the medical provider */
  address?: string | null;
  /** @deprecated Phone number of the medical provider */
  phone?: string | null;
  /** Description of treatment provided */
  treatment?: string | null;
  /** @deprecated Fax number of the medical provider */
  fax?: string | null;
  /** @deprecated Email address of the medical provider */
  email?: string | null;
  /** @deprecated Dates of service provided */
  dates_of_service?: string | null;
  /** Whether the provider is still treating the client */
  treatment_status: ProviderTreatmentStatus;
  /** Provider specialty (free text) */
  specialty?: string | null;
  /** Ideal Treatment Path care level bucket */
  care_level?: TreatmentCareLevel | null;
  /** Confidence (0-1) for care_level when set by AI */
  care_level_confidence?: number | null;
  /** Source of care_level inference */
  care_level_source?: 'AI' | 'MANUAL' | null;
  /** When care_level was last updated */
  care_level_updated_at?: string | null;
  /** Procedure types inferred for this provider */
  procedure_types?: ProcedureType[] | null;
  /** Confidence (0-1) for procedure_types when set by AI */
  procedure_types_confidence?: number | null;
  /** Source of procedure_types inference */
  procedure_types_source?: 'AI' | 'MANUAL' | null;
  /** When procedure_types were last updated */
  procedure_types_updated_at?: string | null;
  /** Type of provider (ORGANIZATION or PRACTITIONER) */
  provider_type?: ProviderType;
  /** Lifecycle status of the provider */
  status?: ProviderStatus;
  /** Names of practitioners at the medical provider */
  practitioner_names?: string[];
  /** Date of records request */
  date_of_records_request?: string | null;
  /** Additional description or notes */
  handles?: ContactHandle[];
  /** Explanation of enrichment results for requesting medical records */
  inference_reasoning?: string;
  /** Alternative contact information possibly disjoint from primary */
  fallback_leads?: ProviderSearchResult[];
  /** Encounters associated with the medical provider */
  encounters?: EncounterSchema[];
  /** Tasks associated with the medical provider */
  tasks?: Task[];
  /** Mentions of the provider */
  mentions?: ProviderMention[];
  /** Linked global provider directory reference (legacy) */
  provider_contact_ref_id?: string | null;
  /** Optional hydrated directory provider summary (legacy) */
  provider_contact_ref?: SavedProviderSummary | null;
  /** Linked v2 directory ProviderService reference */
  provider_service_ref_id?: string | null;
  /** Hydrated provider service summary with portal flags */
  provider_service?: {
    id: string;
    service_type: string;
    provider_type_hint?: string | null;
    portal_eligible?: boolean;
    portal_type?: string[] | null;
  } | null;
  /** Whether this provider has been culled as invalid or duplicate */
  culled?: boolean;
  /** Explanation of why this provider was culled */
  culled_reason?: string | null;
  /** When this provider was culled */
  culled_at?: string | null;
  /** Identity lifecycle status (Unit 10) */
  identity_status?: ProviderIdentityLifecycleStatus | null;
  /** Deadline for identity verification */
  identity_due_at?: string | null;
  /** When identity was resolved */
  identity_resolved_at?: string | null;
  /** Reason identity verification was deferred */
  identity_defer_reason?: string | null;
  /** Compound frequency count (e.g. 2 in '2x per week') */
  treatment_frequency_times?: number | null;
  /** Compound frequency period (DAY, WEEK, MONTH) */
  treatment_frequency_interval?: TreatmentFrequencyInterval | null;
  /** First date of treatment with this provider */
  first_treatment_date?: string | null;
}

export interface ReviewFoundProviderEntry {
  medical_provider_id: string;
  name: string | null;
  status?: ProviderStatus;
  provider_service_ref_id?: string | null;
  review_summary?: string | null;
  latest_mention?: ProviderMention | null;
}

/**
 * The type of provider mention in an artifact.
 */
export enum ProviderMentionType {
  /** Provider where patient received or was scheduled for treatment */
  ENCOUNTER = 'ENCOUNTER',
  /** Provider mentioned without explicitly stated service */
  POTENTIALLY_RELEVANT = 'POTENTIALLY_RELEVANT',
}

/**
 * The status of a draft provider in the system.
 */
export enum MentionedProviderStatus {
  /** Initial state when provider is first detected */
  PENDING = 'PENDING',
  /** Currently being enriched with additional information */
  ENRICHING = 'ENRICHING',
  /** Provider already exists in the matter, no action taken */
  SKIPPED_EXISTS = 'SKIPPED_EXISTS',
  /** Provider has been added to the matter */
  ADDED_TO_MATTER = 'ADDED_TO_MATTER',
  /** Provider has been merged with an existing provider */
  MERGED_WITH_EXISTING = 'MERGED_WITH_EXISTING',
}

/**
 * The type of medical provider.
 */
export enum ProviderType {
  /** Healthcare organization, hospital, clinic, etc. */
  ORGANIZATION = 'ORGANIZATION',
  /** Individual healthcare practitioner  */
  PRACTITIONER = 'PRACTITIONER',
  /** Hospital facility */
  HOSPITAL = 'HOSPITAL',
  /** Emergency Medical Services */
  EMS = 'EMS',
  /** Urgent care facility */
  URGENT_CARE = 'URGENT_CARE',
}

/**
 * The lifecycle status of a medical provider.
 */
export enum ProviderStatus {
  /** Initial state when provider is extracted and needs approval */
  NEEDS_APPROVAL = 'NEEDS_APPROVAL',
  /** Provider has been approved and can have tasks created */
  APPROVED = 'APPROVED',
  /** Provider has been rejected */
  REJECTED = 'REJECTED',
}

/**
 * Identity lifecycle status for provider deduplication (Unit 10).
 * Tracks whether a provider's identity has been verified.
 */
export enum ProviderIdentityLifecycleStatus {
  /** Awaiting identity verification (default for new providers) */
  PENDING = 'PENDING',
  /** Identity verified (manually or via auto-resolution) */
  RESOLVED = 'RESOLVED',
  /** User deferred verification */
  DEFERRED = 'DEFERRED',
  /** Past the 14-day verification deadline */
  OVERDUE = 'OVERDUE',
}

/**
 * Whether the provider is still treating the patient.
 */
export enum ProviderTreatmentStatusOld {
  /** Provider is still actively treating the patient */
  STILL_TREATING = 'Yes',
  /** Provider is no longer treating the patient */
  NOT_TREATING = 'No',
  /** Treatment status is unknown */
  UNKNOWN = '',
}

export enum ProviderTreatmentStatus {
  /** Claimant is still actively receiving treatment */
  TREATING = 'TREATING',
  /** Claimant has completed treatment */
  COMPLETED = 'COMPLETED',
  /** Claimant has been referred to another provider */
  REFERRED_TO = 'REFERRED_TO',
  /** Claimant has deviated from treatment */
  DEVIATING = 'DEVIATING',
  /** Treatment status is unknown */
  UNKNOWN = 'UNKNOWN',
  /** Client never treated at this provider — remove from case */
  NEVER_TREATED = 'NEVER_TREATED',
}

/**
 * The type of contact handle.
 */
export enum ContactHandleType {
  /** Phone number */
  PHONE = 'PHONE',
  /** Email address */
  EMAIL = 'EMAIL',
  /** Website URL */
  WEBSITE = 'WEBSITE',
  /** Fax number */
  FAX = 'FAX',
  /** Other contact method */
  OTHER = 'OTHER',
  /** Mailing address */
  MAILING_ADDRESS = 'MAILING_ADDRESS',
}

/**
 * A source citation for a contact handle.
 */
export interface Source {
  /** URL of the source */
  url: string;
  /** Short display text for the source URL */
  title: string;
}

/**
 * A facet of contact information for a medical provider.
 */
export interface ContactHandle {
  /** Unique identifier for the contact handle */
  id?: string;
  /** Well formatted contact information value (phone, email, website, etc.) */
  value: string;
  /** The type of contact handle */
  handle_type: ContactHandleType;
  /** A short description of the handle (e.g., "Billing Department") */
  name?: string;
  /** Citations for the handle */
  sources?: Source[];
  /** Additional notes about the handle */
  notes?: string;
  /** Whether this is the preferred contact method */
  preferred?: boolean;
  /** Address fields for MAILING_ADDRESS type */
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/**
 * Schema representation of an Encounter.
 */
export interface EncounterSchema {
  /** Unique identifier for the encounter */
  id?: string;
  /** Date of service in ISO format (YYYY-MM-DD), if explicitly stated, null otherwise */
  date: string | null;
  /** List of short phrases describing care provided in the encounter as mentioned in the artifact */
  services: string[];
  /** A very concise description of the encounter, as a one liner that might be used in a medical chronology for the case */
  description: string;
  /** How the encounter was created */
  source?: 'document_extraction' | 'check_in' | 'manual';
  /** Mentions of the encounter in artifacts */
  mentions?: ProviderMention[];
}

/**
 * Base information for a provider mention.
 */
export interface ProviderMention {
  /** Unique identifier for the provider mention */
  id: string;
  /** The ID of the artifact where the mention was found */
  artifact_id: string;
  /** The type of artifact where the mention was found */
  artifact_type: ArtifactType;
  /** The type of provider mention */
  mention_type: ProviderMentionType;
  /** Encounters explicitly mentioned with the provider */
  encounters_mentioned?: EncounterSchema[];
  /** Name of the practitioner if explicitly stated */
  practitioner_name?: string;
  /** Name of the organization if explicitly stated */
  organization_name?: string;
  /** Specialty hint (e.g., "radiology", "orthopedic surgery") */
  specialty_hint?: string;
  /** Location hint (city, neighborhood, street, etc.) */
  location_hint?: string;
  /** Supporting evidence and reasoning to justify the confidence score */
  reasoning: string;
  /** Confidence that provider was mentioned and fields are correctly populated */
  confidence: number;
  /** The status of the mention, e.g. pending, enriched, etc. */
  status?: MentionedProviderStatus;
  /** Excerpts from the artifact that support the mention */
  excerpts: string[];
  /** Reference IDs for citation blocks in the source text that support the extracted information. */
  citation_ids: string[];
  /** The enrichment data for the mention (legacy format) */
  enrichment_data?: ProviderSearch;
  /** Structured enrichment data with citations (new format) */
  enrichment?: ProviderEnrichmentData | null;
  /** Reference to a formal Provider model */
  provider_id?: string;
}

/**
 * Structured output for an enriched provider.
 */
export interface ProviderSearchResult {
  /** Name of the practitioner if explicitly stated */
  practitioner_name?: string;
  /** Name of the organization if explicitly stated */
  organization_name?: string;
  /** Address of the provider */
  address?: string;
  /** Contact information associated with the provider */
  handles: ContactHandle[];
}

/**
 * Structured output for provider search results.
 */
export interface ProviderSearch {
  /** Original query used to find the providers */
  query: string;
  /** Most specified and likely hit for the query */
  primary_hit: ProviderSearchResult;
  /** Useful organizations disjoint from the primary hit */
  fallback_leads: ProviderSearchResult[];
  /** Natural language summary of enrichment results */
  summary_of_enrichment: string;
}

// ---------------------------------------------------------------------------
// Provider Enrichment Types (Parallel.ai Integration)
// ---------------------------------------------------------------------------

/**
 * Web source citation for enrichment data.
 */
export interface WebSource {
  /** Source URL where info was found */
  url: string;
  /** Page title or description */
  title: string;
}

/**
 * Handle type for enrichment data.
 */
export type EnrichmentHandleType =
  | 'PHONE'
  | 'FAX'
  | 'EMAIL'
  | 'WEBSITE'
  | 'MAILING_ADDRESS';

/**
 * Contact handle from provider enrichment with full address support.
 */
export interface EnrichmentHandle {
  /** Handle identifier */
  id: string | null;
  /** Contact value (phone number, email, etc.) */
  value: string | null;
  /** Type of contact handle */
  handle_type: EnrichmentHandleType;
  /** Display name (e.g., "Medical Records Fax") */
  name: string | null;
  /** Additional notes (e.g., "Available 9am-5pm") */
  notes: string | null;
  /** Whether this is the recommended contact method */
  preferred: boolean | null;
  /** Citation sources for this handle */
  sources: WebSource[] | null;
  /** Address line 1 (for MAILING_ADDRESS type) */
  address_line1: string | null;
  /** Address line 2 (for MAILING_ADDRESS type) */
  address_line2: string | null;
  /** City (for MAILING_ADDRESS type) */
  city: string | null;
  /** State (for MAILING_ADDRESS type) */
  state: string | null;
  /** ZIP code (for MAILING_ADDRESS type) */
  zip: string | null;
}

/**
 * Structured enrichment data for a provider mention from Parallel.ai.
 */
export interface ProviderEnrichmentData {
  /** Verified organization name */
  confirmed_organization_name: string | null;
  /** Verified address */
  confirmed_address: string | null;
  /** Whether this is confirmed as a healthcare provider */
  is_healthcare_provider: boolean | null;
  /** Enrichment quality score (0.0 - 1.0) */
  confidence: number;
  /** Best method to request medical records */
  records_request_method_notes: string | null;
  /** Contact handles with citations */
  handles: EnrichmentHandle[];
}

/**
 * Request to merge multiple medical providers.
 */
export interface MergeProviderRequest {
  /** The ID of the destination provider record that will remain after merging */
  dst_provider_id: string;
  /** List of source provider record IDs that will be merged into the destination record */
  src_provider_ids: string[];
}

// ---------------------------------------------------------------------------
// Treatment Check-In & Provider Update Schemas
// ---------------------------------------------------------------------------

/**
 * Lifecycle status of a TreatmentCheckIn record.
 */
export enum TreatmentCheckInStatus {
  DRAFT = 'DRAFT',
  PROCESSING = 'PROCESSING',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  COMPLETED = 'COMPLETED',
}

/**
 * Urgency level flagged after check-in triage.
 */
export enum TreatmentCheckInUrgencyLevel {
  ROUTINE = 'ROUTINE',
  NEEDS_ATTENTION = 'NEEDS_ATTENTION',
  URGENT = 'URGENT',
}

/**
 * Cadence-based treatment frequencies for a provider.
 */
export enum TreatmentFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  MONTHLY = 'MONTHLY',
  AS_NEEDED = 'AS_NEEDED',
  OTHER = 'OTHER',
}

/**
 * Period component for compound treatment frequency (e.g. '2x per WEEK').
 */
export enum TreatmentFrequencyInterval {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
}

/**
 * Subjective progress of treatment.
 */
export enum TreatmentProgress {
  IMPROVING = 'IMPROVING',
  STAGNANT = 'STAGNANT',
  REGRESSING = 'REGRESSING',
}

/**
 * Care level bucket for the Ideal Treatment Path.
 */
export enum TreatmentCareLevel {
  EMERGENCY_CARE = 'EMERGENCY_CARE',
  CONSERVATIVE_CARE = 'CONSERVATIVE_CARE',
  DIAGNOSTIC_TESTING = 'DIAGNOSTIC_TESTING',
  SPECIALIST_CARE = 'SPECIALIST_CARE',
  SURGICAL_CARE = 'SURGICAL_CARE',
}

/**
 * High-level injury type inferred for a matter.
 */
export enum InferredInjuryType {
  SOFT_TISSUE_NECK_BACK = 'SOFT_TISSUE_NECK_BACK',
  SHOULDER_INJURY = 'SHOULDER_INJURY',
  KNEE_INJURY = 'KNEE_INJURY',
  SPINE_DISC_HERNIATION = 'SPINE_DISC_HERNIATION',
  FRACTURE = 'FRACTURE',
  TBI_HEAD_INJURY = 'TBI_HEAD_INJURY',
  MULTIPLE_INJURY = 'MULTIPLE_INJURY',
  UNKNOWN = 'UNKNOWN',
}

export enum ProcedureType {
  ER_VISIT = 'ER_VISIT',
  URGENT_CARE = 'URGENT_CARE',
  CHIROPRACTIC_VISIT = 'CHIROPRACTIC_VISIT',
  PT_VISIT = 'PT_VISIT',
  MRI = 'MRI',
  CT_SCAN = 'CT_SCAN',
  XRAY = 'XRAY',
  EMG_NCV = 'EMG_NCV',
  PAIN_MANAGEMENT_CONSULT = 'PAIN_MANAGEMENT_CONSULT',
  EPIDURAL_INJECTION = 'EPIDURAL_INJECTION',
  NERVE_BLOCK = 'NERVE_BLOCK',
  SURGERY_RECOMMENDED = 'SURGERY_RECOMMENDED',
  SURGERY_PERFORMED = 'SURGERY_PERFORMED',
  ORTHO_CONSULT = 'ORTHO_CONSULT',
  OTHER = 'OTHER',
  UNKNOWN = 'UNKNOWN',
}

/**
 * A snapshot of a provider's status at the time of a treatment check-in.
 */
export interface TreatmentCheckInProviderUpdate {
  id: string;
  created_at: string;
  modified_at: string;
  check_in_id: string;
  provider_id: string | null;
  provider_name: string | null;
  provider_specialty: string | null;
  new_treatment_status: ProviderTreatmentStatus | null;
  new_treatment_status_occurred_at: string | null;
  new_treatment_frequency: TreatmentFrequency | null;
  new_treatment_frequency_times: number | null;
  new_treatment_frequency_interval: TreatmentFrequencyInterval | null;
  new_treatment_frequency_text: string | null;
  new_treatment_progress: TreatmentProgress | null;
  new_pain_level: number | null;
  new_treatment_notes: string | null;
  new_first_treatment_date: string | null;
  is_first_appointment?: boolean;
}

/**
 * Represents a single treatment check-in interaction.
 */
export interface TreatmentCheckIn {
  id: string;
  created_at: string;
  modified_at: string;
  matter_id: string;
  user_id: string | null;
  triggered_by_task_id: string | null;
  source_transcript_id: string | null;
  source_conversation_id: string | null;
  status: TreatmentCheckInStatus;
  general_notes: string | null;
  check_in_time: string | null;
  pain_level: number | null;
  pain_level_note: string;
  urgency_level: TreatmentCheckInUrgencyLevel;
  provider_updates?: TreatmentCheckInProviderUpdate[] | null;
  objectives?: TreatmentCheckInObjective[] | null;
  context?: TreatmentCheckInContext | null;
}

export interface TreatmentCheckInContext {
  days_since_last_check_in?: number | null;
  days_since_loss?: number | null;
  missing_day_30_diagnostic_testing?: boolean | null;
  missing_last_appointment_dates_count?: number;
  procedure_keywords_detected?: string[];
}

/**
 * Payload for creating a provider update within a check-in.
 */
export interface CreateTreatmentCheckInProviderUpdate {
  check_in_id: string;
  provider_id?: string | null;
  provider_name?: string | null;
  new_treatment_status?: ProviderTreatmentStatus | null;
  new_treatment_frequency?: TreatmentFrequency | null;
  new_treatment_frequency_times?: number | null;
  new_treatment_frequency_interval?: TreatmentFrequencyInterval | null;
  new_treatment_frequency_text?: string | null;
  new_treatment_progress?: TreatmentProgress | null;
  new_pain_level?: number | null;
  new_treatment_notes?: string | null;
  new_first_treatment_date?: string | null;
}

/**
 * Payload for creating/updating a provider update within a check-in draft.
 */
export interface SaveCheckInDraftProviderUpdate {
  id: string;
  check_in_id: string;
  provider_id?: string | null;
  provider_name?: string | null;
  new_treatment_status?: ProviderTreatmentStatus | null;
  new_treatment_frequency?: TreatmentFrequency | null;
  new_treatment_frequency_times?: number | null;
  new_treatment_frequency_interval?: TreatmentFrequencyInterval | null;
  new_treatment_frequency_text?: string | null;
  new_treatment_progress?: TreatmentProgress | null;
  new_pain_level?: number | null;
  new_treatment_notes?: string | null;
  new_first_treatment_date?: string | null;
  is_first_appointment?: boolean;
}

/**
 * Payload for saving the draft state of a treatment check-in form.
 */
export interface SaveCheckInDraftPayload {
  id?: string | null;
  check_in_id: string;
  general_notes?: string | null;
  pain_level?: number | null;
  pain_level_note?: string | null;
  updates: SaveCheckInDraftProviderUpdate[];
  objectives?: { id: string; response: string; response_note?: string | null }[];
}

/**
 * Next appointment information extracted from transcript.
 */
export interface NextAppointment {
  scheduled: boolean;
  date_or_timeframe: string | null;
  purpose: string | null;
}

/**
 * Treatment completion information extracted from transcript.
 */
export interface CompletionInfo {
  completion_date: string | null;
  reason: string | null;
}

/**
 * Provider referral information extracted from transcript.
 */
export interface ReferralInfo {
  referred_by: string | null;
  reason: string | null;
}

/**
 * Per-provider extraction from treatment check-in call transcript.
 */
export interface ProviderExtraction {
  provider_name: string;
  provider_type: string | null;
  treatment_status: string | null;
  treatment_frequency: string | null;
  treatment_notes: string | null;
  next_appointment: NextAppointment | null;
  completion_info: CompletionInfo | null;
  referral_info: ReferralInfo | null;
}

/**
 * Severity information for claimant symptoms.
 */
export interface Severity {
  reported: boolean;
  value: number | null;
  descriptor: string | null;
}

/**
 * Functional impact information for claimant.
 */
export interface FunctionalImpact {
  reported: boolean;
  details: string[];
}

/**
 * Overall claimant status extracted from transcript.
 */
export interface HowClaimantFeels {
  summary: string;
  symptoms_reported: string[];
  severity: Severity;
  functional_impact: FunctionalImpact;
}

/**
 * Individual completed treatment item.
 */
export interface TreatmentCompletedItem {
  type: string;
  provider: string | null;
  completion_date: string | null;
}

/**
 * Completed treatments summary.
 */
export interface TreatmentCompleted {
  reported: boolean;
  items: TreatmentCompletedItem[];
}

/**
 * Individual ongoing treatment item.
 */
export interface OngoingTreatmentItem {
  type: string;
  frequency: string | null;
  provider: string | null;
  notes: string | null;
}

/**
 * Ongoing treatments summary.
 */
export interface TreatmentOngoing {
  ongoing_treatment_reported: boolean;
  ongoing_items: OngoingTreatmentItem[];
  next_appointments: NextAppointment[];
}

/**
 * AI-extracted structured summary from treatment check-in call transcript.
 */
export interface TreatmentCheckInAIExtraction {
  id: string;
  check_in_id: string;
  transcript_id: string | null;
  provider_extractions: ProviderExtraction[];
  how_claimant_feels: HowClaimantFeels;
  treatment_completed: TreatmentCompleted;
  treatment_ongoing: TreatmentOngoing;
  extraction_status: 'pending' | 'completed' | 'failed';
  model_used: string;
  error_message: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Treatment Barrier Schemas
// ---------------------------------------------------------------------------

/**
 * How we track treatment compliance issues for a provider.
 */
export enum TreatmentBarrierType {
  PROVIDER_ISSUE = 'PROVIDER_ISSUE',
  SCHEDULING_ISSUE = 'SCHEDULING_ISSUE',
  TRANSPORTATION = 'TRANSPORTATION',
  TREATMENT_ADHERENCE = 'TREATMENT_ADHERENCE',
  BILLING_INSURANCE = 'BILLING_INSURANCE',
  WORSENING_CONDITION = 'WORSENING_CONDITION',
  NEW_INJURY = 'NEW_INJURY',
  OTHER = 'OTHER',
}

/**
 * Current status of a treatment barrier.
 */
export enum TreatmentBarrierStatus {
  ACTIVE = 'ACTIVE',
  RESOLVED = 'RESOLVED',
}

/**
 * Representation of a TreatmentBarrier record.
 */
export interface TreatmentBarrier {
  id: string;
  created_at: string;
  modified_at: string;
  matter_id: string;
  barrier_type: TreatmentBarrierType;
  status: TreatmentBarrierStatus;
  notes: string;
  discovered_in_check_in_id?: string | null;
}

/**
 * Payload for creating a TreatmentBarrier.
 */
export interface CreateTreatmentBarrier {
  matter_id?: string | null;
  barrier_type: TreatmentBarrierType;
  notes: string;
  discovered_in_check_in_id?: string | null;
}

/**
 * Payload for updating a TreatmentBarrier (partial).
 */
export interface UpdateTreatmentBarrier {
  id: string;
  status?: TreatmentBarrierStatus | null;
  notes?: string | null;
}

// ---------------------------------------------------------------------------
// Treatment Check-In Structured Objectives
// ---------------------------------------------------------------------------

/**
 * Type of objective generated for a treatment check-in.
 */
export enum CheckInObjectiveType {
  CONFIRM_STILL_TREATING = 'CONFIRM_STILL_TREATING',
  CONFIRM_TREATMENT_FREQUENCY = 'CONFIRM_TREATMENT_FREQUENCY',
  CONFIRM_FIRST_VISIT = 'CONFIRM_FIRST_VISIT',
  CONFIRM_APPOINTMENT_DATE = 'CONFIRM_APPOINTMENT_DATE',
  CONFIRM_COMPLETION_DATE = 'CONFIRM_COMPLETION_DATE',
  OTHER = 'OTHER',
}

/**
 * Response to a check-in objective.
 */
export enum CheckInObjectiveResponse {
  PENDING = 'PENDING',
  YES = 'YES',
  NO = 'NO',
  COULD_NOT_COLLECT = 'COULD_NOT_COLLECT',
}

/**
 * A structured objective for a treatment check-in.
 */
export interface TreatmentCheckInObjective {
  id: string;
  created_at: string;
  modified_at: string;
  check_in_id: string;
  provider_id: string | null;
  provider_name: string | null;
  objective_type: CheckInObjectiveType;
  label: string;
  response: CheckInObjectiveResponse;
  response_note: string | null;
  sort_order: number;
  source: string;
}

// ---------------------------------------------------------------------------
// Treatment Check-In Objectives (AI-Generated / Legacy)
// ---------------------------------------------------------------------------

/**
 * Metadata about AI-generated check-in objectives.
 */
export interface CheckInObjectivesMetadata {
  treating_count: number;
  unknown_count: number;
  completed_count: number;
  review_count: number;
}

/**
 * AI-generated objectives for a treatment check-in call.
 */
export interface CheckInObjectives {
  objectives: string[];
  metadata: CheckInObjectivesMetadata;
  generated_at: string;
  source: 'ai' | 'fallback';
}
